import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, ArrowRight, Loader2, Package, Zap } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { API_URL } from "../config/api";


const SubscriptionSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, token, refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [error, setError] = useState(null);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      verifySubscription();
    } else {
      setLoading(false);
    }
  }, [sessionId]);

  const verifySubscription = async () => {
    try {
      // Verify the subscription status
      const response = await fetch(`${API_URL}/subscriptions/verify/${sessionId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      if (response.ok) {
        const data = await response.json();
        setSubscription(data);
        
        // Refresh user context to get updated permissions
        if (refreshUser) {
          await refreshUser();
        }
      } else {
        // Even if verify fails, show success (webhook may not have processed yet)
        setSubscription({ status: 'processing' });
      }
    } catch (err) {
      console.error('Verification error:', err);
      setSubscription({ status: 'processing' });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-white text-lg">Verifying your subscription...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center p-6">
      <div className="max-w-lg w-full">
        {/* Success Card */}
        <div className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-2xl p-8 text-center">
          {/* Success Icon */}
          <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-green-500/25">
            <CheckCircle className="w-10 h-10 text-white" />
          </div>

          <h1 className="text-3xl font-bold text-white mb-2">
            Subscription Activated!
          </h1>
          
          <p className="text-slate-400 mb-8">
            Thank you for subscribing to myEuropacific. Your modules are now active.
          </p>

          {/* Subscription Details */}
          {subscription?.modules && (
            <div className="bg-slate-900/50 rounded-xl p-4 mb-8 text-left">
              <p className="text-slate-400 text-sm mb-3">Your active modules:</p>
              <div className="flex flex-wrap gap-2">
                {(subscription.modules || []).map(module => (
                  <span 
                    key={module}
                    className="inline-flex items-center gap-1.5 bg-blue-500/20 text-blue-300 px-3 py-1.5 rounded-full text-sm"
                  >
                    <Package className="w-4 h-4" />
                    {module}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Processing Notice */}
          {subscription?.status === 'processing' && (
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 mb-8">
              <div className="flex items-center gap-2 text-yellow-400 text-sm">
                <Zap className="w-4 h-4" />
                Your subscription is being processed. This may take a few moments.
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white py-3 px-6 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all"
            >
              Go to Dashboard
              <ArrowRight className="w-5 h-5" />
            </button>
            
            <button
              onClick={() => navigate('/pricing')}
              className="w-full bg-slate-700 hover:bg-slate-600 text-white py-3 px-6 rounded-lg font-medium transition-all"
            >
              Manage Subscription
            </button>
          </div>
        </div>

        {/* Help Text */}
        <p className="text-slate-500 text-sm text-center mt-6">
          Need help? Contact us at{' '}
          <a href="mailto:support@europacific.com" className="text-blue-400 hover:underline">
            support@europacific.com
          </a>
        </p>
      </div>
    </div>
  );
};

export default SubscriptionSuccessPage;
